import api from '@/services/apiService';
import type { BadmintonGeneratePayload } from '@/components/community/badminton/badmintonGenerateUtils';

export interface BadmintonAccess {
  canEdit: boolean;
  canRead: boolean;
}

export interface BadmintonSetScore {
  id?: number;
  gameId?: number;
  setNumber: number;
  scoreSideA: number;
  scoreSideB: number;
  isComplete: boolean;
  elapsedMs?: number;
}

export interface BadmintonGamePlayerItem {
  participantId: number;
  playerName: string;
  avatarUrl?: string;
  side: 'a' | 'b';
  slotOrder: number;
}

export type BadmintonTimerStatus = 'idle' | 'running' | 'paused';
export type BadmintonTimerAction = 'start' | 'pause' | 'reset';

export interface BadmintonGameListItem {
  id: number;
  slug: string;
  sessionId: number;
  sessionSlug: string;
  sessionTitle: string;
  sessionType: string;
  rotationFormat?: string;
  roundNumber?: number;
  courtNumber?: number;
  sessionNumCourts?: number;
  playerFormat: string;
  scoringType: string;
  maxSets: number;
  status: string;
  winnerSide?: string;
  sideAPlayers: BadmintonGamePlayerItem[];
  sideBPlayers: BadmintonGamePlayerItem[];
  setScores: BadmintonSetScore[];
  timerStatus: BadmintonTimerStatus | string;
  timerSetNumber: number;
  timerElapsedMs: number;
  timerStartedAt?: string;
  serverTime: string;
}

export interface BadmintonSessionListItem {
  id: number;
  slug: string;
  title: string;
  sessionType: string;
  playerFormat: string;
  rotationFormat: string;
  scoringType: string;
  defaultMaxSets: number;
  numCourts: number;
  numRounds: number;
  maxGamesPerPlayer: number;
  maxStayWins: number;
  teams: number[][];
  queueParticipantIds: number[];
  status: string;
  createdAt?: string;
  games?: BadmintonGameListItem[];
}

export interface BadmintonRoundDetail {
  roundNumber: number;
  status: string;
  queueParticipantIds: number[];
  games?: BadmintonGameListItem[];
  game?: BadmintonGameListItem;
}

export interface BadmintonSessionDetail extends BadmintonSessionListItem {
  rounds: BadmintonRoundDetail[];
}

export interface BadmintonStandingItem {
  position: number;
  participantId: number;
  playerName: string;
  avatarUrl?: string;
  gamesPlayed: number;
  wins: number;
  losses: number;
  winRate: number;
  setsWon: number;
  setsLost: number;
  pointsScored: number;
  pointsConceded: number;
}

export interface BadmintonTopMmrItem {
  position: number;
  participantId: number;
  playerName: string;
  avatarUrl?: string;
  deltaMmr: number;
  wins: number;
  losses: number;
  setsWon: number;
  setsLost: number;
}

export interface BadmintonGameDetail extends BadmintonGameListItem {
  eventSlug: string;
  canEdit: boolean;
}

export interface BadmintonParticipantOption {
  id: number;
  name: string;
}

const badmintonService = {
  async getAccess(eventSlug: string): Promise<BadmintonAccess> {
    const res = await api.get(`/mabar/events/${eventSlug}/badminton/access`);
    return res.data.data;
  },

  async listSessions(eventSlug: string): Promise<BadmintonSessionListItem[]> {
    const res = await api.get(`/mabar/events/${eventSlug}/badminton/sessions`);
    return res.data.data ?? [];
  },

  async getStandings(eventSlug: string): Promise<BadmintonStandingItem[]> {
    const res = await api.get(`/mabar/events/${eventSlug}/badminton/standings`);
    return res.data.data ?? [];
  },

  async getTopMmr(eventSlug: string): Promise<BadmintonTopMmrItem[]> {
    const res = await api.get(`/mabar/events/${eventSlug}/badminton/top-mmr`);
    return res.data.data ?? [];
  },

  async generateSession(
    eventSlug: string,
    payload: BadmintonGeneratePayload
  ): Promise<BadmintonSessionDetail> {
    const res = await api.post(`/mabar/events/${eventSlug}/badminton/sessions/generate`, payload);
    return res.data.data;
  },

  async createManualGame(
    eventSlug: string,
    payload: {
      playerFormat: string;
      scoringType?: string;
      maxSets?: number;
      sideA: number[];
      sideB: number[];
    }
  ): Promise<BadmintonGameDetail> {
    const res = await api.post(
      `/mabar/events/${eventSlug}/badminton/sessions/manual-game`,
      payload
    );
    return res.data.data;
  },

  async getGame(gameSlug: string): Promise<BadmintonGameDetail> {
    const res = await api.get(`/mabar/badminton/games/${gameSlug}`);
    return res.data.data;
  },

  async getSessionDetail(sessionSlug: string): Promise<BadmintonSessionDetail> {
    const res = await api.get(`/mabar/badminton/sessions/${sessionSlug}`);
    return res.data.data;
  },

  async updateScores(
    gameSlug: string,
    sets: { setNumber: number; scoreSideA: number; scoreSideB: number }[]
  ): Promise<BadmintonGameDetail> {
    const res = await api.patch(`/mabar/badminton/games/${gameSlug}/scores`, { sets });
    return res.data.data;
  },

  async updateTimer(
    gameSlug: string,
    payload: { action: BadmintonTimerAction; setNumber: number }
  ): Promise<BadmintonGameDetail> {
    const res = await api.patch(`/mabar/badminton/games/${gameSlug}/timer`, payload);
    return res.data.data;
  },

  async updateMaxSets(gameSlug: string, maxSets: number): Promise<BadmintonGameDetail> {
    const res = await api.patch(`/mabar/badminton/games/${gameSlug}/max-sets`, { maxSets });
    return res.data.data;
  },

  async deleteGame(gameSlug: string): Promise<void> {
    await api.delete(`/mabar/badminton/games/${gameSlug}`);
  },

  async deleteSession(sessionSlug: string): Promise<void> {
    await api.delete(`/mabar/badminton/sessions/${sessionSlug}`);
  },

  async resetEventGames(eventSlug: string): Promise<void> {
    await api.post(`/mabar/events/${eventSlug}/badminton/sessions/reset`);
  },

  async updateRoundLineup(
    sessionSlug: string,
    roundNumber: number,
    payload: { courtNumber?: number; sideA: number[]; sideB: number[]; queue?: number[] }
  ): Promise<BadmintonSessionDetail> {
    const res = await api.patch(
      `/mabar/badminton/sessions/${sessionSlug}/rounds/${roundNumber}/lineup`,
      payload
    );
    return res.data.data;
  },

  async updateGameLineup(
    gameSlug: string,
    payload: { sideA: number[]; sideB: number[] }
  ): Promise<BadmintonGameDetail> {
    const res = await api.patch(`/mabar/badminton/games/${gameSlug}/lineup`, payload);
    return res.data.data;
  },
};

export default badmintonService;
